import { PosthogFeatureFlags } from '@domgen/shared/enums/featureFlags';
import { calculateAgeInMonths } from '@domgen/shared/utils/date/calculateAgeInMonths';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { usePostHog, useFeatureFlagEnabled } from '@posthog/react';
import { useState, useEffect } from 'react';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { StoreEvent } from '../../../enums/storeEvent';
import { QuoteResponse } from '../../../models/services/api/postQuote';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { useApplianceFormData } from '../../../store/selectors/useApplianceFormData';
import { useAppState } from '../../../store/selectors/useAppState';
import { useGaEventData } from '../../../store/selectors/useGaEventData';
import { useQuote } from '../../../store/selectors/useQuote';
import { useUrlParams } from '../../../store/selectors/useUrlParams';
import { store } from '../../../store/store';
import { getDiscountDisplayData } from '../../../utils/discounts/getDiscountDisplayData';
import { getSystemCode } from '../../../utils/getSystemCode';
import { formApplianceName } from '../../../utils/store/formApplianceName';
import { formCategoryName } from '../../../utils/store/formCategoryName';
import { CoverageBenefitsPanel } from '../components/CoverageBenefitsPanel/CoverageBenefitsPanel';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { Modal } from '@domgen/base-design-system-2.0/molecules/Modal';
import { OfferPanel } from '../components/OfferPanel';
import { quoteModalContent } from '../constants/quoteModalContent';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { coverageBenefitsPanelArticles } from '../constants/coverageBenefitsPanelArticles';
import { ArrowLeft } from '../../../assets/icons/ArrowLeft';
import { ApplianceCareTerms } from '../components/ApplianceCareTerms';
import { gaPageView, gaEvent, gaECommerceEvent } from '@domgen/shared/services/ga/ga';
import { useNavigate } from 'react-router-dom';
import { controlRoutes } from '../../../routes/controlRoutes';

const styles = {
  applianceCareTermsContainer: 'col-span-12 flex justify-start mb-space-400',
  container: 'flex flex-col items-center bg-bg-main',
  panel:
    'grid grid-cols-12 max-w-300 p-space-500 mb-space-600 mx-space-500 gap-space-600 w-full bg-white',
  headerRow: 'col-span-12 text-center',
  chooseApplianceCareContainer:
    'w-full col-span-12 flex flex-col justify-center items-center gap-space-500 px-space-700 pt-space-700 mb-space-800',
  chooseApplianceCareArticles:
    'flex flex-col desktop:flex-row justify-center items-center gap-space-500 desktop:mx-space-400 ',
  offerPanelModal: 'desktop:max-w-88.75',
  buttonLayout: 'flex flex-col gap-space-500 desktop:flex-row desktop:justify-center',
  buttonLayoutModal:
    'flex flex-col w-full justify-center gap-space-500 desktop:flex-row desktop:items-center',
  buttonLayoutSideDraw: 'w-full flex flex-col gap-space-500 p-space-500 ',
};

/**
 * This is  the variant_1_minimal for Posthog in quote-page-jul-2026 experiment.
 */
export function QuotePageMinimal() {
  const quote = useQuote();
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const [displayModal, setDisplayModal] = useState(false);
  const posthog = usePostHog();
  const quoteData = quote?.data as QuoteResponse;
  const gaEventData = useGaEventData() || {};
  const applianceName = formApplianceName.get() ?? '';
  const applianceCategory = formCategoryName.get() ?? '';
  const brand = import.meta.env.VITE_CLIENT_FRIENDLY_NAME;
  const appSessionID = session?.id;
  const { experimentName, experimentVariant, netFirstPaymentAmount: offerPrice } = gaEventData;
  const purchaseDate = formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE];
  const show2MonthsPromotion =
    useFeatureFlagEnabled(PosthogFeatureFlags.TWO_MONTHS_PROP) &&
    !!purchaseDate &&
    calculateAgeInMonths(purchaseDate) < 3;
  const discountTerms = getDiscountDisplayData(quoteData);
  const discountIsAch = discountTerms.discountPaymentType === 'ach';
  const navigate = useNavigate();

  useEffect(() => {
    gaPageView({
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appSessionID: session?.id,
      pagename: 'protection-details',
      offerPrice,
      experimentName,
      experimentVariant,
      quoteId: quoteData.offer.offerReference,
    });
  }, []);

  const toggleModal = () => {
    capturePostHogEvent(posthog, 'protection-details-page-register-only-cta-clicked', store.get());

    setDisplayModal((prevDisplayModal) => {
      // Fire gaEvent only when the modal is being opened
      if (!prevDisplayModal) {
        gaEvent({
          event: 'genericGAEvent',
          eventCategory: 'appliance details form USA',
          eventAction: 'protectionDetails',
          eventLabel: 'No, I want to register with warranty only',
          pagename: 'protection-details',
          category: 'registration-pages',
          type: 'STANDARD',
          regSystemCode: systemCode,
          applianceCategory,
          applianceName,
          appSessionID,
          offerPrice,
          experimentName,
          experimentVariant,
          quoteId: quoteData.offer.offerReference,
        });

        gaPageView({
          event: 'spa-pageview',
          pagename: 'protection-details-popup',
          category: 'registration-pages',
          type: 'STANDARD',
          regSystemCode: systemCode,
          offerPrice,
          applianceName,
          applianceCategory,
          appSessionID,
          experimentName,
          experimentVariant,
          quoteId: quoteData.offer.offerReference,
        });

        gaEvent({
          event: 'genericGAEvent',
          eventCategory: 'appliance details form USA',
          eventAction: 'protectionDetails_popup_upsell',
          eventLabel: 'popup_presented',
          pagename: 'protection-details-popup',
          category: 'registration-pages',
          type: 'STANDARD',
          offerPrice,
          regSystemCode: systemCode,
          applianceCategory,
          applianceName,
          appSessionID,
          experimentName,
          experimentVariant,
          quoteId: quoteData.offer.offerReference,
        });
      }
      return !prevDisplayModal;
    });
  };

  const registerOnly = () => {
    store.send({ type: StoreEvent.REG_ONLY });
    capturePostHogEvent(posthog, 'protection-details-modal-register-only-cta-clicked', store.get());
    //Register only event only continues from the modal
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'protection_details_popup_upsell',
      eventLabel: 'No, register with warranty only',
      pagename: 'protection-details-popup',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      experimentName,
      experimentVariant,
      quoteId: quoteData.offer.offerReference,
    });

    setDisplayModal(false);
  };

  const continueToCheckout = () => {
    store.send({
      type: StoreEvent.CONTINUE_TO_CHECKOUT,
      data: { continueToCheckoutFromModal: displayModal },
    });

    capturePostHogEvent(posthog, 'protection-details-page-monthly-upsell-cta-clicked', store.get());

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: displayModal ? 'protectionDetails_popup_upsell' : 'protectionDetails',
      eventLabel: 'Yes, I want this protection',
      pagename: displayModal ? 'protection-details-popup' : 'protection-details',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      experimentName,
      experimentVariant,
      quoteId: quoteData.offer.offerReference,
    });

    gaECommerceEvent({
      event: 'add_to_cart',
      pagename: displayModal ? 'protection-details-popup' : 'protection-details',
      regSystemCode: systemCode,
      version: 'WiB',
      currency: 'USD',
      appSessionID,
      experimentName,
      experimentVariant,
      quoteId: quoteData.offer.offerReference,
      ecommerce: {
        items: [
          {
            item_brand: brand,
            item_category: applianceCategory,
            item_appliance_type: applianceName,
            item_appliance_purchase_cost: Number(
              formData?.[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
            ),
            item_appliance_purchase_date: formatLocalisedDate(
              parseLocalDateString(formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] || ''),
              'en-US',
            ),
            item_appliance_warranty: true,
            item_name: `${brand} ${applianceName}`,
            price: offerPrice,
            quantity: 1,
            quoteId: quoteData.offer.offerReference,
          },
        ],
      },
    });
  };

  const handleBack = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      pagename: 'protection-details',
      eventAction: 'GoBack CTA',
      eventLabel: 'success',
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
      experimentName,
      experimentVariant,
      quoteId: quoteData.offer.offerReference,
    });
    capturePostHogEvent(posthog, 'price-back-clicked', store.get());
    navigate(controlRoutes[controlRoutes.length - 1].path);
  };

  return (
    <>
      <div className={styles.container} data-testid="quote-page-minimal">
        <div className={styles.panel}>
          <div className={styles.headerRow}>
            <h1 className="mb-space-400 text-h2">
              Your manufacturer's warranty may not cover your next repair
            </h1>
            <p className="text-body">
              Get the protection you need today, now discounted to $
              {discountTerms.netFirstPaymentAmount} per month (down from $
              {discountTerms.netFirstPaymentAmountAfterPromotionalPeriod}){' '}
              {discountIsAch ? '*' : ''}
            </p>
          </div>

          <section className="col-span-12 ">
            <CoverageBenefitsPanel
              periodOfCover={discountTerms.periodOfCover}
              discountPercentage={discountTerms.discountPercentage}
              discountTerm={discountTerms.discountTerm}
              netFirstPaymentAmount={discountTerms.netFirstPaymentAmount}
              netFirstPaymentAmountAfterPromotionalPeriod={
                discountTerms.netFirstPaymentAmountAfterPromotionalPeriod
              }
              discountPaymentType={discountTerms.discountPaymentType}
              show2MonthsPromotion={show2MonthsPromotion}
              coverageBenefitsPanelArticles={coverageBenefitsPanelArticles}
            />
          </section>

          <div className={styles.applianceCareTermsContainer}>
            <ApplianceCareTerms
              href={import.meta.env.VITE_APPLIANCE_CARE_TERMS_AND_CONDITIONS_PATH}
              brand={import.meta.env.VITE_CLIENT_FRIENDLY_NAME}
            />
          </div>

          <div className="col-span-12 flex flex-col-reverse gap-space-300 desktop:flex-row">
            <Button
              variant="outline"
              onClick={() => {
                toggleModal();
              }}
            >
              Continue with registration only
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                continueToCheckout();
              }}
            >
              Add {brand} Appliance Care
            </Button>
          </div>

          <div className="col-span-12 flex justify-center">
            <TextLink onClick={handleBack} leftIcon={<ArrowLeft />}>
              Go Back
            </TextLink>
          </div>
        </div>
      </div>

      <Modal isOpen={displayModal} onClose={toggleModal}>
        <h1 className="text-h5  mb-space-400">{quoteModalContent.title}</h1>
        <p className="mb-space-400">{quoteModalContent.description}</p>
        <p className="text-body-strong mb-space-200">{quoteModalContent.benefits.heading}</p>
        <ul className="list-none mb-4 text-left">
          {quoteModalContent.benefits.benefitsList.map((benefit, index) => (
            <li key={index} className="flex items-center mb-2">
              <span className="mr-2">{<benefit.icon />}</span>
              <span>{benefit.text}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-col items-center gap-space-400 ">
          <div className={styles.offerPanelModal}>
            <OfferPanel
              periodOfCover={discountTerms.periodOfCover}
              discountPercentage={discountTerms.discountPercentage}
              discountTerm={discountTerms.discountTerm}
              netFirstPaymentAmount={discountTerms.netFirstPaymentAmount}
              netFirstPaymentAmountAfterPromotionalPeriod={
                discountTerms.netFirstPaymentAmountAfterPromotionalPeriod
              }
              discountPaymentType={discountTerms.discountPaymentType}
              show2MonthsPromotion={show2MonthsPromotion}
            />
          </div>

          <div className={styles.buttonLayoutModal}>
            <Button
              variant="outline"
              onClick={() => {
                registerOnly();
              }}
            >
              No thanks, register only
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                continueToCheckout();
              }}
            >
              Register with extra coverage
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
